/**
 * The tenant's running S3 instances, for the S3 connection on Configure.
 *
 * An instance counts when its lease is active and it runs one of the S3
 * instance images. What it runs is known only in this browser: the signed SDL
 * (`deployedSdl`) when this browser rented it, which also carries its keys,
 * or else the image recorded with its label. The chain holds neither.
 */

import type { DeploymentLabel } from '../core/deploymentLabels'
import { isS3InstanceImage, type S3InstanceCandidate } from '../core/s3Connection'
import { s3InstanceFromSdl } from '../core/s3ConnectionSdl'
import { leaseIdOf } from './adapters'
import type { LeaseDto } from './types'

/** Where one candidate's lease lives, for reading its status. */
export interface S3InstanceLease {
  provider: string
  gseq: number
  oseq: number
}

export function s3InstanceCandidates(
  leases: readonly LeaseDto[],
  readSdl: (dseq: string) => string | null,
  labels: Record<string, DeploymentLabel>,
): Array<S3InstanceCandidate & { lease: S3InstanceLease }> {
  const seen = new Set<string>()
  const out: Array<S3InstanceCandidate & { lease: S3InstanceLease }> = []
  for (const item of leases) {
    const id = leaseIdOf(item)
    if (!id?.dseq || item.lease?.state !== 'active' || seen.has(id.dseq) || !id.provider) continue
    const label = labels[id.dseq]
    const sdl = readSdl(id.dseq)
    const fromSdl = sdl ? s3InstanceFromSdl(sdl) : null
    if (!fromSdl && !(label?.image && isS3InstanceImage(label.image))) continue
    seen.add(id.dseq)
    out.push({
      dseq: id.dseq,
      name: label?.name ?? `Instance #${id.dseq}`,
      // The guided form names its one service `app`.
      service: fromSdl?.service ?? 'app',
      credentials: fromSdl?.credentials ?? null,
      lease: { provider: id.provider, gseq: Number(id.gseq ?? 1) || 1, oseq: Number(id.oseq ?? 1) || 1 },
    })
  }
  return out
}
